/**
 * TrackIconDialog
 * 
 * @Author: Focci
 * @Date: 2023-10-24 14:14:49
 * @Last Modified by: Focci
 * @Last Modified time: 2023-10-24 14:14:49
 */

import PropTypes from 'prop-types'
import useTrackStatus from '@hook/useTrackStatus'
import SVGUnclaimed from '@img/unclaimed.svg'
import SVGClaimed from '@img/claimed.svg'
import { ButtonBase, CircularProgress, Tooltip } from '@comp/material'
import clsx from 'clsx'
import { useCallback, useState } from 'react'
import { fetchDelete, fetchProxy } from '@lib/fetch/client'
import { useTranslations } from 'next-intl'
import useLogin from '@hook/useLogin'
import { PropertyTrackDialog } from '.'

function TrackDialogButton({
  propertyId,
  onChange = () => {}
}) {
  const tp = useTranslations('profile')
  const tt = useTranslations('track')
  const [loading, setLoading] = useState(false)
  const { ready, isTracked, removeSubcribe } = useTrackStatus(propertyId)

  const handleClick = useCallback(() => {
    if(isTracked) {
      fetchProxy(fetchDelete(`property-claim/${propertyId}`), setLoading)
        .then(() => {
          removeSubcribe()
          onChange({ type: 'close' })
        })
    } else {
      onChange({ type: 'open' })
    }
  }, [isTracked, onChange, propertyId, removeSubcribe])

  if(!ready) {
    return null
  }

  return (
    <Tooltip
      arrow
      title={isTracked ? tp('cancelClaim') : tt('claim')}
      placement="top" 
    >
      <ButtonBase 
        className="rounded-full"
        disabled={loading}
        onClick={handleClick}
      >
        <div
          className={clsx([
            'h-9 w-9 rounded-full flex items-center justify-center',
            isTracked ? 'bg-primary text-white' : 'bg-white'
          ])}
        >
          {loading && (
            <CircularProgress 
              size=".7rem" 
              sx={{ color: isTracked ? 'white' : '#20a4f8' }} 
            />
          )}
          {!loading && (
            <span className="-mt-0.5 -mr-[1px]">
              {
                isTracked 
                  ? <SVGClaimed width=".8rem" /> 
                  : <SVGUnclaimed width=".8rem" />
              }
            </span>
          )}
        </div>
      </ButtonBase>
    </Tooltip>
  )
}

export default function TrackIconDialog({
  propertyId
}) {
  const [open, setOpen] = useState(false)
  const { isLogin, openSigninDialog } = useLogin()

  const handleChange = useCallback(({ type }) => {
    if(type === 'close') {
      setOpen(false)
    } else if(!isLogin) {
      openSigninDialog()
    } else {
      setOpen(true)
    }
  }, [isLogin, openSigninDialog])

  return (
    <>
      <TrackDialogButton 
        propertyId={propertyId}
        onChange={handleChange}
      />
      <PropertyTrackDialog
        open={open}
        onClose={() => setOpen(false)}
        propertyId={propertyId}
      />
    </>
  )
}

TrackIconDialog.propTypes = {
  propertyId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
}
